import { useState, useEffect, useContext } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import BasicsContext from '@shared/context/BasicsContext';

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Hook to display real-time duration counter for an activity
 * Format: mm:ss or h:mm:ss if duration > 60 minutes
 *
 * @param {string} startedAt - ISO timestamp of when activity started
 * @param {boolean} isRunning - Whether the activity is currently running
 * @param {number} timezoneId - Timezone ID of the activity
 * @returns {string} Formatted duration string (e.g., "05:30", "1:23:45")
 */
export const useActivityTimer = (startedAt, isRunning, timezoneId) => {
  const [displayTime, setDisplayTime] = useState('00:00');
  const basics = useContext(BasicsContext);

  useEffect(() => {
    if (!isRunning || !startedAt) {
      setDisplayTime('00:00');
      return;
    }

    const updateTimer = () => {
      const timezoneName = basics?.timezones?.find(tz => tz.id === timezoneId)?.name;
      const start = timezoneName ? dayjs.tz(startedAt, timezoneName) : dayjs(startedAt);
      const now = dayjs();
      const diffInSeconds = now.diff(start, 'seconds');

      const hours = Math.floor(diffInSeconds / 3600);
      const minutes = Math.floor((diffInSeconds % 3600) / 60);
      const seconds = diffInSeconds % 60;

      let formattedTime;
      if (hours > 0) {
        // Format: h:mm:ss
        formattedTime = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      } else {
        // Format: mm:ss
        formattedTime = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }

      setDisplayTime(formattedTime);
    };

    updateTimer();

    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [startedAt, isRunning, timezoneId, basics?.timezones]);

  return displayTime;
};
